<template>
  <div class="col-12 " :dir="locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ $t('classes.breadcrumbs.editClass') }}</h6>
          </div>
        </div>
      </div>
   
      <LoadingSpinner v-if="isLoading" :message="$t('loading.message')" />

      <div v-else class="card-body px-0 pb-2 mx-2">
        <div class="row justify-content-center">
          <div class="col-md-6">
            <ClassForm 
              ref="classForm"
              :initial-data="initialClassData"
              :is-edit="true"
              @submit="handleSubmit" 
              @cancel="handleCancel" 
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />
            
            <div class="d-flex gap-3 mt-3">
              <button 
                type="button" 
                class="btn btn-primary btn-sm flex-grow-1"
                @click="submitForm"
                :disabled="isSubmitting || !hasChanges"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('classes.messages.saving') }}
                </span>
                <span v-else>
                  <i class="fas fa-edit" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('classes.actions.edit') }}
                </span>
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="handleCancel"
                :disabled="isSubmitting"
              >
                <i class="fas fa-times" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                {{ $t('baseDialog.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ClassForm from '@/components/features/classes/forms/ClassForm.vue';
import { useToast } from 'vue-toastification'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t, locale } = useI18n();
const toast = useToast()
const router = useRouter();
const route = useRoute();
const store = useStore();
const classForm = ref(null);
const isSubmitting = ref(false);
const isLoading = ref(true);
const initialClassData = ref({ 
  name: '', 
  is_active: true,
});
const originalClassData = ref({});
const currentFormData = ref({});

const hasChanges = computed(() => {
  return (
    currentFormData.value.name !== originalClassData.value.name ||
    currentFormData.value.is_active !== originalClassData.value.is_active
  );
});

const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
};

onMounted(async () => {
  try {
    const classId = route.params.id;
    const response = await store.dispatch('classes/fetchClass', classId);
    initialClassData.value = { 
      name: response.data.name,
      is_active: response.data.is_active,
    };
    originalClassData.value = { ...initialClassData.value };
    currentFormData.value = { ...initialClassData.value };
  } catch (error) {
    console.error('Error fetching class:', error);
    toast.error(t('classes.messages.loadError'));
    router.push('/classes');
  } finally {
    isLoading.value = false;
  }
});

const handleSubmit = async (formData) => {
  isSubmitting.value = true;
  try {
    const classId = route.params.id;
    await store.dispatch('classes/updateClass', { 
      classId: classId, 
      classData: formData 
    });
    toast.success(t('classes.messages.editSuccess'));
    router.push('/classes');
  } catch (error) {
    if (error.response?.data?.errors) {
      classForm.value.setServerErrors(error.response.data.errors);
      toast.error(t('classes.messages.validationError'));
    } else {
      toast.error(t('classes.messages.editError'));
    }
    console.error('Error updating class:', error);
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = () => {
  if (!hasChanges.value) {
    toast.info(t('classes.messages.noChanges'));
    return;
  }
  classForm.value.submit();
};

const handleValidationError = () => {
  toast.warning(t('classes.messages.validationError'));
};

const handleCancel = () => {
  router.push('/classes');
};
</script>